import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const PROJECT_BASE_URL: string =
  import.meta.env.VITE_TEST_FRESHERS_PROJECT_BASE || "";

export const ProjectGeneratorApiSlice = createApi({
  reducerPath: "ProjectGeneratorApi",
  baseQuery: fetchBaseQuery({ baseUrl: PROJECT_BASE_URL }),
  endpoints: (builder) => ({
    getProjectOptions: builder.query<any, string>({
      query: (language) => `api/generator/options/${language.toUpperCase()}`,
    }),
    searchMavenDependencies: builder.query<
      any,
      { q: string; rows: number; core: string }
    >({
      query: ({ q, rows, core }) =>
        `api/generator/maven-search?q=${encodeURIComponent(q)}&rows=${rows}&core=${core.toLowerCase()}`,
    }),
    searchReactDependencies: builder.query<
      any,
      { q: string; size?: number; from?: number }
    >({
      query: ({ q, size = 15, from = 0 }) =>
        `api/react-dependencies/search?q=${encodeURIComponent(q)}&size=${size}&from=${from}`,
    }),
    getReactDependencyDetails: builder.query<any, string | { name: string }>({
      query: (arg) => {
        const name = typeof arg === "string" ? arg : arg.name;
        return `api/react-dependencies/details?name=${encodeURIComponent(name)}`;
      },
    }),
    getReactPackageDetails: builder.query<any, string | { name: string }>({
      query: (arg) => {
        const name = typeof arg === "string" ? arg : arg.name;
        return `api/react-dependencies/details?name=${encodeURIComponent(name)}`;
      },
    }),
    generateProject: builder.mutation<ArrayBuffer, any>({
      query: (body) => ({
        url: "api/generator/generate",
        method: "POST",
        body,
        responseHandler: (response) => response.arrayBuffer(),
      }),
    }),
    generateGradleProject: builder.mutation<ArrayBuffer, any>({
      query: (body) => ({
        url: "api/generator/generate-gradle",
        method: "POST",
        body,
        responseHandler: (response) => response.arrayBuffer(),
      }),
    }),
    uploadStudentProject: builder.mutation<
      any,
      { questionId: string; file: FormData }
    >({
      query: ({ questionId, file }) => ({
        url: `api/question-projects-db/${encodeURIComponent(questionId)}/student-project`,
        method: "POST",
        body: file,
      }),
    }),
    uploadTestProject: builder.mutation<
      any,
      { questionId: string; file: FormData }
    >({
      query: ({ questionId, file }) => ({
        url: `api/question-projects-db/${encodeURIComponent(questionId)}/test-project`,
        method: "POST",
        body: file,
      }),
    }),
    evaluateProject: builder.mutation<
      any,
      { formData: FormData; endpoint: string }
    >({
      query: ({ formData, endpoint }) => ({
        url: endpoint,
        method: "POST",
        body: formData,
        formData: true,
      }),
    }),
    generateTestingProject: builder.mutation<
      ArrayBuffer,
      { language: string; tool: string }
    >({
      query: (body) => ({
        url: "v1/testing-tool/generate",
        method: "POST",
        body,
        responseHandler: (response) => response.arrayBuffer(),
      }),
    }),
    startBrowserSession: builder.mutation<any, void>({
      query: () => ({
        url: "api/browser-sessions/start",
        method: "POST",
      }),
    }),
    stopBrowserSession: builder.mutation<any, void>({
      query: () => ({
        url: "api/browser-sessions/active",
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useGetProjectOptionsQuery,
  useLazyGetProjectOptionsQuery,
  useLazySearchMavenDependenciesQuery,
  useLazySearchReactDependenciesQuery,
  useGetReactDependencyDetailsQuery,
  useLazyGetReactDependencyDetailsQuery,
  useGetReactPackageDetailsQuery,
  useLazyGetReactPackageDetailsQuery,
  useGenerateProjectMutation,
  useGenerateGradleProjectMutation,
  useGenerateTestingProjectMutation,
  useUploadStudentProjectMutation,
  useUploadTestProjectMutation,
  useEvaluateProjectMutation,
  useStartBrowserSessionMutation,
  useStopBrowserSessionMutation,
} = ProjectGeneratorApiSlice;
